/**
 * Presenca ao vivo — contrato das mensagens do canal e regras puras de
 * agrupamento.
 *
 * A presenca tem DUAS fontes que nao se confundem: quem existe (permanente,
 * vem do cadastro) e quem esta conectado agora (efemero, morre com o
 * processo). A lista renderizada e sempre a primeira; a segunda so classifica.
 */

/**
 * Uma pessoa do workspace, como o painel e o perfil precisam dela.
 *
 * `hasAvatar` e `avatarVersion` acompanham a mesma regra do resto do app
 * (`sessionAvatarSrc` na web): foto enviada vence a url externa, e a versao e
 * o cache-buster que faz a troca de foto aparecer sem recarregar.
 */
export type MembroPresenca = {
  id: string
  name: string
  email: string
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
  companyAreaId: string | null
  isActive: boolean
  /** Perfil personalizado (2026-09-16): apresentacao curta e banner atras da foto. */
  bio: string | null
  hasBanner: boolean
}

/**
 * O que a pessoa ESCOLHE no menu do perfil. Persiste no cadastro.
 *
 * `offline` e "aparecer offline": ela continua conectada e recebendo tudo, mas
 * os colegas a veem como quem fechou o app.
 */
export const STATUS_ESCOLHIVEIS = ['disponivel', 'ausente', 'offline'] as const
export type StatusEscolhido = (typeof STATUS_ESCOLHIVEIS)[number]

/**
 * O que os colegas VEEM. Deriva da escolha + das sessoes abertas.
 *
 * `desconectado` nao e escolhivel: e a ausencia de sessao. Separar de `offline`
 * importa para o servidor, embora a lista desenhe os dois no mesmo grupo.
 */
export type EstadoPresenca = StatusEscolhido | 'desconectado' | 'no-computador'

/**
 * O que UMA sessao reporta sobre a pessoa (SPEC-20260924-0924).
 *
 * `no-orbind`: houve mouse/teclado NA JANELA do Orbind. E a presenca mais
 * forte que existe — a pessoa esta olhando para c.
 *
 * `no-computador`: a janela esta parada, mas o SISTEMA diz que houve entrada
 * ha pouco. So o desktop sabe disso: o navegador nao expoe atividade fora da
 * aba, por seguranca, e nem deveria. Vale o caso comum de quem trabalha o dia
 * todo com o Orbind na bandeja — antes disso, essa pessoa virava "ausente"
 * mesmo digitando sem parar em outro programa.
 *
 * `parado`: nem na janela nem no computador, pelo tempo configurado.
 */
export const ATIVIDADES = ['no-orbind', 'no-computador', 'parado'] as const
export type Atividade = (typeof ATIVIDADES)[number]

/**
 * Onde a sessao esta rodando, para a lista poder mostrar.
 *
 * `mobile` sai do navegador do celular; o app nativo, quando existir, entra
 * aqui tambem. O desktop se identifica sozinho, porque o shell Tauri esta
 * presente.
 */
export const PLATAFORMAS = ['desktop', 'navegador', 'mobile'] as const
export type Plataforma = (typeof PLATAFORMAS)[number]

/** Como alguem aparece para os outros, agora. */
export type PresencaDeMembro = {
  memberId: string
  estado: EstadoPresenca
  /**
   * Por onde ela esta conectada AGORA — uma pessoa pode estar em duas ao mesmo
   * tempo (desktop no escritorio, celular no bolso), e a lista mostra as duas.
   */
  plataformas: Plataforma[]
  /**
   * A ATIVIDADE crua, separada do que ela declarou (SPEC-20260924-0924).
   *
   * Quem se declara AUSENTE continua aparecendo como ausente — a palavra dela
   * vale —, mas a lista tambem mostra que ela esta no computador. Sao duas
   * informacoes diferentes e ambas interessam a quem vai chamar.
   *
   * Em OFFLINE isto vem sempre `parado`: "aparecer offline" nao pode vazar que
   * a pessoa esta la. E a diferenca entre declarar-se ausente (educado) e
   * declarar-se fora (privado).
   */
  atividade: Atividade
  /**
   * O que a pessoa DECLAROU, quando isso muda o que os outros veem.
   *
   * `null` quando ela nao declarou nada de diferente — assim a lista so mostra
   * o selo quando ha uma declaracao de verdade por tras.
   */
  declarado: StatusEscolhido | null
  /**
   * O PROGRAMA em uso no computador agora — "Excel", "Google Chrome"
   * (SPEC-20261020-2341-programa-em-uso-na-presenca, pedido do Cristian).
   *
   * Só o NOME do programa: nunca o título da janela nem o arquivo aberto. Só
   * existe quando a pessoa está "no computador" pelo Orbind Desktop no Windows;
   * fica `null` quando ela está no próprio Orbind, parada, ou se declarou
   * ausente ou offline — declarar-se fora não pode vazar o que ela faz.
   */
  programa: string | null
}

/** Maior nome de programa aceito do cliente — o resto é cortado. */
export const TAMANHO_MAXIMO_DO_PROGRAMA = 60

/**
 * Limpa o nome do programa vindo do cliente: só texto visível, sem quebras,
 * no máximo TAMANHO_MAXIMO_DO_PROGRAMA. Vazio vira `null`.
 */
export function limparNomeDoPrograma(valor: unknown): string | null {
  if (typeof valor !== 'string') return null
  const limpo = valor.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim()
  return limpo === '' ? null : limpo.slice(0, TAMANHO_MAXIMO_DO_PROGRAMA)
}

/**
 * Pacote de abertura: a VERDADE INTEIRA no instante em que a sessao comeca.
 *
 * Nao e incremento. Ao receber, o cliente SUBSTITUI o mapa de presencas por
 * completo — e so isso conserta o estado depois de uma queda de conexao,
 * porque quem entrou, saiu ou trocou de status durante a queda nunca gerou
 * aviso para este cliente.
 */
export type AberturaPresenca = {
  tipo: 'abertura'
  voce: string
  /** So quem NAO esta `desconectado` viaja aqui; o resto se deduz. */
  presencas: readonly PresencaDeMembro[]
  /**
   * Quantos minutos parado ate virar ausente, definido no Console.
   *
   * Viaja com a abertura em vez de ter endpoint proprio: e o unico parametro
   * que o cliente precisa para o relogio de ociosidade, e chegar junto da
   * conexao garante que ele nunca roda com valor velho.
   */
  minutosAteAusente: number
}

/** O estado de alguem mudou: entrou, saiu, ficou ausente ou trocou a escolha. */
export type AvisoPresenca = {
  tipo: 'presenca'
  presenca: PresencaDeMembro
}

/**
 * Mexeram numa conversa de que EU participo.
 *
 * Carrega so o ponteiro, nao a mensagem: serializar a mensagem aqui duplicaria
 * a montagem que o servico ja faz (autor, anexos, reacoes, citacao) e as duas
 * copias divergiriam na primeira mudanca de formato. O cliente rebusca.
 */
export type AvisoChat = {
  tipo: 'chat'
  channelId: string
  messageId: string
  acao: 'nova' | 'atualizada'
}

/**
 * Meus contatos de fora do workspace mudaram: chegou convite, aceitaram,
 * recusaram ou cancelaram. So o ponteiro — o cliente rebusca.
 */
export type AvisoContato = {
  tipo: 'contato'
}

/**
 * Alguem esta digitando numa conversa de que EU participo.
 *
 * Efemero: nada vai para o banco. Quem recebe apaga sozinho depois de
 * `DIGITANDO_EXPIRA_MS` sem novo aviso — nao existe "parou de digitar", porque
 * uma aba fechada no meio da frase nunca mandaria esse quadro.
 */
export type AvisoDigitando = {
  tipo: 'digitando'
  channelId: string
  memberId: string
  nome: string
}

/**
 * Alguem me mencionou (@Nome) numa conversa de que participo. Carrega o
 * suficiente para o aviso (quem, onde, trecho) sem o cliente rebuscar nada.
 */
export type AvisoMencao = {
  tipo: 'mencao'
  channelId: string
  messageId: string
  autor: string
  /** Foto de quem mandou — o cartão de aviso mostra o rosto, não um ícone. */
  foto: FotoDoAutorDoAviso
  canal: string
  trecho: string
}

/** O mínimo para montar a URL da foto (mesma regra do resto do app). */
export type FotoDoAutorDoAviso = {
  memberId: string
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
}

/**
 * Mensagem nova numa conversa que EU não silenciei (pedido 2026-09-14). Vai
 * para quem participa, menos o autor, quem silenciou e quem foi mencionado
 * (esse recebe o `mencao`, que avisa mesmo com a conversa silenciada).
 */
export type AvisoMensagem = {
  tipo: 'mensagem'
  channelId: string
  messageId: string
  autor: string
  foto: FotoDoAutorDoAviso
  /** Nome do grupo; em conversa direta, vazio (o autor já diz tudo). */
  canal: string
  direta: boolean
  trecho: string
}

/* --------------------------------------------------- videochamada --- */

/**
 * Sinais da videochamada da conversa direta (pedido 2026-09-14).
 *
 * O servidor NAO participa da chamada: so repassa estes sinais entre as duas
 * pessoas (quem liga, quem atende) pelo canal ao vivo que ja existe. Imagem e
 * voz vao direto de um navegador ao outro (WebRTC).
 *
 * - `convite`   toca do outro lado (de vídeo ou só de voz)
 * - `midia`     liguei/desliguei a câmera ou comecei/parei de compartilhar a tela
 * - `aceitar` / `recusar` / `ocupado` / `indisponivel` (este vem do servidor)
 * - `cancelar`  quem ligou desistiu antes de atenderem
 * - `encerrar`  qualquer um desligou
 * - `oferta` / `resposta` / `ice` a negociacao da conexao
 */
export type SinalDeChamada =
  /** `video: false` = chamada de voz (a câmera pode ser ligada depois). */
  | { acao: 'convite'; video: boolean }
  /** O que estou mandando agora: câmera ligada e/ou tela compartilhada. */
  | { acao: 'midia'; video: boolean; tela: boolean }
  | { acao: 'aceitar' }
  | { acao: 'recusar' }
  | { acao: 'ocupado' }
  | { acao: 'indisponivel' }
  | { acao: 'cancelar' }
  | { acao: 'encerrar' }
  | { acao: 'oferta'; sdp: string }
  | { acao: 'resposta'; sdp: string }
  | { acao: 'ice'; candidato: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null } }
  /**
   * Esta chamada tem uma SALA (SPEC-20260917-0047): quem atende entra nela
   * também, senão não enxergaria quem chega pelo link do convite.
   */
  | { acao: 'sala'; roomId: string }

export const ACOES_DE_CHAMADA = [
  'convite',
  'midia',
  'aceitar',
  'recusar',
  'ocupado',
  'indisponivel',
  'cancelar',
  'encerrar',
  'oferta',
  'resposta',
  'ice',
  'sala',
] as const

/** Quanto tempo o telefone toca antes de desistir. */
export const CHAMADA_TOCA_POR_MS = 45_000

/**
 * Um sinal que chegou. `sessao` identifica a JANELA de quem mandou: a mesma
 * pessoa pode estar com duas abertas, e so a que atendeu segue na chamada — as
 * outras param de tocar.
 */
export type AvisoChamada = {
  tipo: 'chamada'
  callId: string
  channelId: string
  de: string
  deNome: string
  sessao: string
  sinal: SinalDeChamada
}

/* ------------------------------------------ sala com portaria --- */

/**
 * Sala de chamada (SPEC-20260917-0047).
 *
 * A chamada de duas pessoas é endereçada por MEMBRO; a sala não pode ser, porque
 * o convidado por link não tem conta. Aqui tudo é endereçado pelo id do
 * PARTICIPANTE, que vale só dentro da sala e some com ela.
 */
export type ParticipanteDaSala = {
  /** Endereço dentro da sala. Quem sai e volta recebe outro. */
  id: string
  nome: string
  /** Entrou por link, sem conta no Orbind. */
  convidado: boolean
  memberId: string | null
}

/** O que dois participantes trocam para conectar a mídia entre eles. */
export type SinalDaSala =
  | { acao: 'oferta'; sdp: string }
  | { acao: 'resposta'; sdp: string }
  | { acao: 'ice'; candidato: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null } }
  | { acao: 'midia'; video: boolean; tela: boolean }

export const ACOES_DA_SALA = ['oferta', 'resposta', 'ice', 'midia'] as const

/**
 * O que um participante conta para a sala inteira de uma vez (SPEC-20261010-0157):
 * o próprio estado (microfone, mão levantada), o chat da reunião e as reações.
 * Vale só enquanto a chamada existe: nada disso é gravado.
 */
export type DadoDaSala =
  | { tipo: 'estado'; mic: boolean; mao: boolean }
  | { tipo: 'chat'; id: string; texto: string }
  | { tipo: 'reacao'; emoji: string }

export const TIPOS_DE_DADO_DA_SALA = ['estado', 'chat', 'reacao'] as const
export const MAX_TEXTO_NO_CHAT_DA_SALA = 2000

/**
 * O que o servidor conta sobre a sala.
 *
 * `entrei` chega para quem acabou de entrar e traz QUEM JÁ ESTAVA: é esse lado
 * que faz a oferta para cada um. Quem já estava recebe `entrou` e espera a
 * oferta — assim nunca acontece de os dois lados ofertarem ao mesmo tempo.
 */
export type AvisoDaSala = {
  tipo: 'sala'
  roomId: string
  evento:
    | { nome: 'entrei'; eu: ParticipanteDaSala; participantes: ParticipanteDaSala[] }
    | { nome: 'entrou'; participante: ParticipanteDaSala }
    | { nome: 'saiu'; participanteId: string }
    | { nome: 'sinal'; de: string; sinal: SinalDaSala }
    /** Algo que um participante contou para todos (inclusive para ele mesmo). */
    | { nome: 'dado'; de: string; dado: DadoDaSala }
    /** Alguém bateu na porta — só o dono recebe. */
    | { nome: 'portaria'; guestId: string; visitante: string; recado: string | null }
    /** A decisão sobre MIM — só o convidado que esperava recebe. */
    | { nome: 'decisao'; aprovado: boolean }
    | { nome: 'encerrada' }
}

/** O super admin mudou uma configuracao da instalacao. */
export type AvisoConfiguracao = {
  tipo: 'configuracao'
  minutosAteAusente: number
}

/**
 * NOTIFICACAO da pessoa (SPEC-20260924-2111).
 *
 * Diferente de `mensagem`/`mencao`, que falam de conversa: esta fala do
 * trabalho. Vai com o cartao pronto — codigo, titulo, quem fez, quando — para
 * o cliente desenhar o aviso sem uma consulta a mais.
 */
export type AvisoNotificacao = {
  tipo: 'notificacao'
  id: string
  /** Hoje so `work_item_assigned`; a lista cresce (comentario, prazo). */
  kind: string
  workItemId: string | null
  /** Codigo do Item de Trabalho — e por ele que a pessoa reconhece o item. */
  code: string
  titulo: string
  /** Quem causou. */
  autor: string
  criadaEm: string
  /**
   * Convite para evento do Calendário (`calendar_invite`, SPEC-20261027-2350-calendario-real):
   * o evento e o dia dele, para o aviso levar direto ao dia certo.
   */
  calendarEventId?: string | null
  data?: string | null
}

/** O cadastro de alguem mudou: nome, avatar, area. */
export type AvisoPerfil = {
  tipo: 'perfil'
  membro: MembroPresenca
}

/** Tudo que o servidor manda pelo canal. */
export type MensagemDoServidor =
  | AberturaPresenca
  | AvisoPresenca
  | AvisoPerfil
  | AvisoConfiguracao
  | AvisoChat
  | AvisoContato
  | AvisoDigitando
  | AvisoMencao
  | AvisoMensagem
  | AvisoNotificacao
  | AvisoChamada
  | AvisoDaSala

/**
 * Tudo que o cliente manda.
 *
 * O token vai no PRIMEIRO QUADRO, nunca na URL: query string entra em log de
 * servidor e de proxy, e um JWT em log e credencial vazada.
 *
 * `ocioso` e por SESSAO, nao por pessoa: quem deixou o desktop parado mas esta
 * mexendo no celular continua disponivel. Quem decide e o cliente, porque so
 * ele ve mouse e teclado.
 */
export type MensagemDoCliente =
  /** `plataforma` e opcional: cliente antigo nao manda, e vira 'navegador'. */
  | { tipo: 'autenticar'; token: string; plataforma?: Plataforma }
  | { tipo: 'status'; status: StatusEscolhido }
  /** `programa`: só o desktop manda, e só em `no-computador` (SPEC-20261020-2341-programa-em-uso-na-presenca). */
  | { tipo: 'atividade'; atividade: Atividade; programa?: string | null }
  /**
   * Formato ANTIGO, ainda aceito (2026-09-24).
   *
   * Uma aba aberta antes da atualizacao continua mandando isto por horas. Sem
   * traduzir, essa pessoa ficaria presa no estado em que estava. `true` vira
   * `parado`, `false` vira `no-orbind`.
   */
  | { tipo: 'ocioso'; ocioso: boolean }
  | { tipo: 'digitando'; channelId: string }
  | { tipo: 'chamada'; callId: string; channelId: string; para: string; sessao: string; sinal: SinalDeChamada }
  /** Convidado por link: a credencial dele é o token da portaria, não um JWT. */
  | { tipo: 'autenticar-convidado'; token: string }
  | { tipo: 'sala-entrar'; roomId: string }
  | { tipo: 'sala-sair' }
  | { tipo: 'sala'; para: string; sinal: SinalDaSala }
  | { tipo: 'sala-dado'; dado: DadoDaSala }
  /** Só o dono da sala: deixa entrar ou barra quem bateu na porta. */
  | { tipo: 'sala-decidir'; guestId: string; aprovar: boolean }

/**
 * Ordena por nome ignorando caixa e acento.
 *
 * Ordenar sem normalizar joga "ana" depois de "Ze". `localeCompare` com
 * sensitivity base resolve os dois de uma vez, em pt-BR.
 */
export function compararPorNome(a: MembroPresenca, b: MembroPresenca): number {
  return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
}

/** Rotulo em pt-BR de cada estado, para a lista e o menu. */
export const ROTULO_DO_ESTADO: Record<EstadoPresenca, string> = {
  disponivel: 'Disponível',
  /** Mexendo no computador, com o Orbind fora de vista (SPEC-20260924-0924). */
  'no-computador': 'No computador',
  ausente: 'Ausente',
  offline: 'Offline',
  desconectado: 'Desconectado',
}

/** O estado de alguem, com `desconectado` como padrao de quem nao aparece. */
export function estadoDe(
  memberId: string,
  presencas: ReadonlyMap<string, PresencaDeMembro>,
): EstadoPresenca {
  return presencas.get(memberId)?.estado ?? 'desconectado'
}

/**
 * Separa os membros nos TRES grupos do painel.
 *
 * DERIVADO, nunca guardado: o cruzamento e refeito a cada render a partir das
 * duas fontes. Guardar os grupos criaria uma terceira verdade para
 * desincronizar.
 *
 * Ausente tem grupo PROPRIO (decisao do usuario, 2026-09-12 04:50): a categoria
 * ja diz o estado, sem precisar de marcacao extra na linha.
 *
 * Quem escolheu `offline` cai junto dos desconectados de proposito — e o
 * sentido de "aparecer offline". Para os colegas ele e indistinguivel de quem
 * fechou o app, que e exatamente o pedido.
 */
export function agruparPorEstado(
  membros: readonly MembroPresenca[],
  presencas: ReadonlyMap<string, PresencaDeMembro>,
): { disponiveis: MembroPresenca[]; ausentes: MembroPresenca[]; desconectados: MembroPresenca[] } {
  const disponiveis: MembroPresenca[] = []
  const ausentes: MembroPresenca[] = []
  const desconectados: MembroPresenca[] = []
  for (const membro of membros) {
    // Identidade SEMPRE pelo id: nome muda, e duas pessoas podem escolher o mesmo.
    const estado = estadoDe(membro.id, presencas)
    /*
     * Quem esta NO COMPUTADOR entra com os disponiveis (SPEC-20260924-0924):
     * ela esta trabalhando e da para chamar agora. O ponto mais fraco e o
     * rotulo dizem que nao esta olhando para o Orbind, sem esconde-la num
     * grupo de baixo.
     */
    if (estado === 'disponivel' || estado === 'no-computador') disponiveis.push(membro)
    else if (estado === 'ausente') ausentes.push(membro)
    else desconectados.push(membro)
  }
  return {
    disponiveis: disponiveis.sort(compararPorNome),
    ausentes: ausentes.sort(compararPorNome),
    desconectados: desconectados.sort(compararPorNome),
  }
}

/**
 * Ha id conectado que nao existe na lista?
 *
 * Acontece quando alguem se cadastra com a sessao ja aberta: o aviso de
 * presenca chega, mas a lista veio da carga inicial e nao tem essa pessoa —
 * ela apareceria conectada e sem nome. Quem chamar deve rebuscar a lista.
 */
export function haConectadoDesconhecido(
  membros: readonly MembroPresenca[],
  presencas: ReadonlyMap<string, PresencaDeMembro>,
): boolean {
  const conhecidos = new Set(membros.map((m) => m.id))
  for (const id of presencas.keys()) if (!conhecidos.has(id)) return true
  return false
}
